// screen: Companies (v3 §3.1 entity-index, control plane internal/moments) — the tenant's company dictionary: search,
// mention counts and the programmes each company appears in; rename, aliases, ticker, status; merge two companies,
// split one, delete; and the company page (/company/{slug}) per site. Every change is audited and re-links the
// mentions on the next index pass. Nothing here changes a site until it has a company template.
// v3 §3.2: mention alerts — the tenant switch, window and hourly cap, subscriber counts, a company's subscribers
// (addresses masked, CSV export, remove) and erase-by-address. Off by default.
import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { Bell, Building2, Download, GitMerge, Scissors, Trash2, X } from 'lucide-react';
import type { Scope, SiteRow } from '@viewstream/api-client';
import { Badge, Button, Dialog, DialogContent, Field, Input, Select, Skeleton, Switch, useToast } from '@viewstream/ui';
import { fmtDateTime } from '@viewstream/i18n';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { can } from '~/app/permissions';
import { problemText } from '~/app/team';
import { clock } from '~/app/ai';
import { lz, usePageActions, usePages, useSites } from '~/app/sites';
import {
  COMPANY_TEMPLATE_DRAFT, alertSubscribersCsvPath, parseList, useEntities, useEntity, useEntityAlertCounts, useEntityAlertMutations, useEntityAlertSettings,
  useEntityAlertSubscribers, useEntityMutations, type EntityAlertStatus, type EntityDetail, type EntityInput, type EntityRow, type EntityStatus,
} from '~/app/entities';
import { downloadFile } from '~/app/billing';

interface EntitiesSearch { id?: string; q?: string }

export const Route = createFileRoute('/_layout/t/$slug/entities')({
  component: Screen,
  validateSearch: (s: Record<string, unknown>): EntitiesSearch => ({
    ...(typeof s['id'] === 'string' && /^[0-9a-f-]{36}$/.test(s['id']) ? { id: s['id'] } : {}),
    ...(typeof s['q'] === 'string' && s['q'] ? { q: String(s['q']).slice(0, 100) } : {}),
  }),
});

const WRITE = 'assets:write' as Scope;

function useStatusLabel() {
  const { t } = useLingui();
  return (s: EntityStatus) => (s === 'confirmed' ? t`Confirmed` : s === 'unconfirmed' ? t`Unconfirmed` : t`Disabled`);
}

function Screen() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [q, setQ] = React.useState(search.q ?? '');
  const list = useEntities(tenant.slug, search.q ?? '');
  const statusLabel = useStatusLabel();
  React.useEffect(() => { document.title = appTitle(t`Companies`); }, [t]);
  const go = (next: EntitiesSearch) => void navigate({ to: '/t/$slug/entities' as never, params: { slug: tenant.slug } as never, search: next as never });
  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold"><Building2 className="size-6" aria-hidden />{t`Companies`}</h1>
        <p className="mt-1 max-w-3xl text-sm text-fg-muted">{t`The companies your Hebrew transcripts are matched against: names, aliases and tickers. A change here re-links the mentions in every programme within a few minutes and is recorded in the audit log. Programme pages show the companies as chips; a site with a company page template also gets a page per company.`}</p>
      </div>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        <div className="space-y-4">
          <form role="search" className="flex gap-2" onSubmit={(e) => { e.preventDefault(); go({ q: q.trim() || undefined, id: search.id }); }}>
            <Input aria-label={t`Search companies`} placeholder={t`Name, alias or ticker`} value={q} onChange={(e) => setQ(e.target.value)} />
            <Button type="submit" size="md" variant="secondary">{t`Search`}</Button>
          </form>
          {list.isLoading ? <Skeleton className="h-60" /> : list.isError ? <p className="text-sm text-danger">{t`Could not load the companies.`}</p> : (
            <ul className="max-h-[70vh] space-y-1 overflow-auto" aria-label={t`Companies`}>
              {(list.data ?? []).map((e) => (
                <li key={e.id}>
                  <button type="button" onClick={() => go({ id: e.id, q: search.q })} aria-current={search.id === e.id ? 'true' : undefined}
                    className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-start text-sm ${search.id === e.id ? 'bg-primary/10 text-primary' : 'hover:bg-surface-2'}`}>
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{e.name_he}</span>
                      <span className="block truncate text-xs text-fg-muted">{e.name_en}{e.ticker ? <> · <bdi dir="ltr">{e.exchange ? `${e.exchange}: ` : ''}{e.ticker}</bdi></> : null}</span>
                    </span>
                    <span className="flex shrink-0 flex-col items-end gap-1">
                      <span className="text-xs text-fg-muted">{t`${e.mentions} mentions · ${e.programmes} programmes`}</span>
                      {e.status !== 'confirmed' ? <Badge tone={e.status === 'unconfirmed' ? 'warn' : 'neutral'}>{statusLabel(e.status)}</Badge> : null}
                    </span>
                  </button>
                </li>
              ))}
              {list.data?.length === 0 ? <li className="text-sm text-fg-muted">{search.q ? t`No company matches “${search.q}”.` : t`No companies yet.`}</li> : null}
            </ul>
          )}
          {can(scopes, WRITE) ? <CreateEntity onCreated={(id) => go({ id })} /> : null}
          <CompanyTemplates />
          <AlertsSettings onOpen={(id) => go({ id, q: search.q })} />
        </div>
        {search.id ? <EntityDetailPanel key={search.id} id={search.id} all={list.data ?? []} onGone={() => go({ q: search.q })} onOpen={(id) => go({ id, q: search.q })} />
          : <p className="text-sm text-fg-muted">{t`Pick a company to see its mentions and edit it.`}</p>}
      </div>
    </div>
  );
}

function CreateEntity({ onCreated }: { onCreated: (id: string) => void }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const toast = useToast();
  const m = useEntityMutations(tenant.slug);
  const [he, setHe] = React.useState('');
  const [en, setEn] = React.useState('');
  const [slug, setSlug] = React.useState('');
  const [ticker, setTicker] = React.useState('');
  return (
    <form className="space-y-2 rounded-md border border-border p-3" onSubmit={(e) => {
      e.preventDefault();
      m.create.mutate({ name_he: he, name_en: en, slug, ticker }, {
        onSuccess: (x) => { setHe(''); setEn(''); setSlug(''); setTicker(''); toast.push({ title: t`Company added`, tone: 'ok' }); onCreated(x.id); },
        onError: (err) => toast.push({ title: t`Could not add the company`, description: problemText(err), tone: 'danger' }),
      });
    }}>
      <h2 className="text-sm font-semibold">{t`Add a company`}</h2>
      <Field id="ent-new-he" label={t`Hebrew name`}><Input value={he} onChange={(e) => setHe(e.target.value)} required /></Field>
      <Field id="ent-new-en" label={t`English name`}><Input value={en} onChange={(e) => setEn(e.target.value)} dir="ltr" /></Field>
      <Field id="ent-new-slug" label={t`Slug`} hint={t`Lowercase letters, digits and dashes; the address of the company page.`}>
        <Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} dir="ltr" required pattern="[a-z0-9][a-z0-9\-]{0,63}" />
      </Field>
      <Field id="ent-new-ticker" label={t`Ticker`}><Input value={ticker} onChange={(e) => setTicker(e.target.value.toUpperCase())} dir="ltr" /></Field>
      <Button type="submit" size="sm" loading={m.create.isPending} disabled={!he || !slug}>{t`Add`}</Button>
    </form>
  );
}

/** One alias list (chips with remove, plus an input that takes "a, b, c"). */
function AliasEditor({ id, label, hint, value, onChange, disabled }: { id: string; label: string; hint?: string; value: string[]; onChange: (v: string[]) => void; disabled?: boolean }) {
  const { t } = useLingui();
  const [draft, setDraft] = React.useState('');
  const add = () => { const xs = parseList(draft); if (xs.length) onChange([...value, ...xs.filter((x) => !value.includes(x))]); setDraft(''); };
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-sm font-medium">{label}</label>
      {hint ? <p className="text-xs text-fg-muted">{hint}</p> : null}
      <ul className="flex flex-wrap gap-1" aria-label={label}>
        {value.map((a) => (
          <li key={a}><Badge tone="primary">{a}{disabled ? null : (
            <button type="button" className="ms-1" aria-label={t`Remove ${a}`} onClick={() => onChange(value.filter((x) => x !== a))}><X className="size-3" aria-hidden /></button>
          )}</Badge></li>
        ))}
        {value.length === 0 ? <li className="text-xs text-fg-muted">{t`None`}</li> : null}
      </ul>
      {disabled ? null : (
        <div className="flex gap-2">
          <Input id={id} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={t`Add, separated by commas`}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
          <Button type="button" size="md" variant="secondary" onClick={add} disabled={!draft.trim()}>{t`Add`}</Button>
        </div>
      )}
    </div>
  );
}

function EntityDetailPanel({ id, all, onGone, onOpen }: { id: string; all: EntityRow[]; onGone: () => void; onOpen: (id: string) => void }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const q = useEntity(tenant.slug, id);
  if (q.isLoading) return <Skeleton className="h-96" />;
  if (!q.data) return <p className="text-sm text-danger">{t`Could not load the company.`}</p>;
  return <EntityEditor key={q.data.entity.updated_at ?? ''} d={q.data} all={all} onGone={onGone} onOpen={onOpen} />;
}

function EntityEditor({ d, all, onGone, onOpen }: { d: EntityDetail; all: EntityRow[]; onGone: () => void; onOpen: (id: string) => void }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { locale } = useLocale();
  const toast = useToast();
  const m = useEntityMutations(tenant.slug);
  const statusLabel = useStatusLabel();
  const e = d.entity;
  const ro = !can(scopes, WRITE);
  const [f, setF] = React.useState<Required<Omit<EntityInput, 'status'>> & { status: EntityStatus }>({
    slug: e.slug, name_he: e.name_he, name_en: e.name_en, aliases: e.aliases, ambiguous: e.ambiguous, ticker: e.ticker ?? '', exchange: e.exchange ?? '', status: e.status,
  });
  const [picked, setPicked] = React.useState<Set<number>>(new Set());
  const [mergeFrom, setMergeFrom] = React.useState('');
  const [splitOpen, setSplitOpen] = React.useState(false);
  const dirty = JSON.stringify(f) !== JSON.stringify({ slug: e.slug, name_he: e.name_he, name_en: e.name_en, aliases: e.aliases, ambiguous: e.ambiguous, ticker: e.ticker ?? '', exchange: e.exchange ?? '', status: e.status });
  const save = () => m.update.mutate({ id: e.id, body: f }, {
    onSuccess: () => toast.push({ title: t`Saved. The mentions are re-linked within a few minutes.`, tone: 'ok' }),
    onError: (err) => toast.push({ title: t`Could not save`, description: problemText(err), tone: 'danger' }),
  });
  const others = all.filter((x) => x.id !== e.id);
  const o = { locale, tz: 'Asia/Jerusalem' };
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold">{e.name_he}{e.name_en ? <span className="ms-2 text-base font-normal text-fg-muted" dir="ltr">{e.name_en}</span> : null}</h2>
        <span className="flex gap-2">
          <Badge tone={e.status === 'confirmed' ? 'ok' : e.status === 'unconfirmed' ? 'warn' : 'neutral'}>{statusLabel(e.status)}</Badge>
          <Badge>{t`${e.mentions} mentions`}</Badge>
        </span>
      </div>

      <section className="grid gap-3 rounded-md border border-border p-4 sm:grid-cols-2" aria-label={t`Details`}>
        <Field id="ent-he" label={t`Hebrew name`}><Input value={f.name_he} onChange={(x) => setF({ ...f, name_he: x.target.value })} disabled={ro} /></Field>
        <Field id="ent-en" label={t`English name`}><Input value={f.name_en} onChange={(x) => setF({ ...f, name_en: x.target.value })} dir="ltr" disabled={ro} /></Field>
        <Field id="ent-slug" label={t`Slug`} hint={t`The company page address: /company/${f.slug}`}>
          <Input value={f.slug} onChange={(x) => setF({ ...f, slug: x.target.value.toLowerCase() })} dir="ltr" disabled={ro} />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field id="ent-ticker" label={t`Ticker`}><Input value={f.ticker} onChange={(x) => setF({ ...f, ticker: x.target.value.toUpperCase() })} dir="ltr" disabled={ro} /></Field>
          <div className="space-y-1">
            <span className="text-sm font-medium">{t`Exchange`}</span>
            <Select ariaLabel={t`Exchange`} value={f.exchange || 'none'} onValueChange={(v) => setF({ ...f, exchange: v === 'none' ? '' : v })}
              options={[{ value: 'none', label: t`None` }, { value: 'TASE', label: 'TASE' }, { value: 'NASDAQ', label: 'NASDAQ' }, { value: 'NYSE', label: 'NYSE' }]} />
          </div>
        </div>
        <div className="space-y-1">
          <span className="text-sm font-medium">{t`Status`}</span>
          <Select ariaLabel={t`Status`} value={f.status} onValueChange={(v) => setF({ ...f, status: v as EntityStatus })}
            options={[{ value: 'confirmed', label: t`Confirmed — matched and shown` }, { value: 'unconfirmed', label: t`Unconfirmed — not matched yet` }, { value: 'disabled', label: t`Disabled — never matched` }]} />
        </div>
        <div className="sm:col-span-2">
          <AliasEditor id="ent-aliases" label={t`Aliases`} hint={t`Other ways the company is said; each counts anywhere in a transcript (whole words, with one Hebrew prefix letter).`}
            value={f.aliases} onChange={(v) => setF({ ...f, aliases: v })} disabled={ro} />
        </div>
        <div className="sm:col-span-2">
          <AliasEditor id="ent-ambiguous" label={t`Ambiguous aliases`} hint={t`Names that are also ordinary words (טבע, לאומי): they count only with stock-market words around them.`}
            value={f.ambiguous} onChange={(v) => setF({ ...f, ambiguous: v })} disabled={ro} />
        </div>
        {ro ? null : (
          <div className="flex flex-wrap gap-2 sm:col-span-2">
            <Button onClick={save} loading={m.update.isPending} disabled={!dirty}>{t`Save`}</Button>
            {dirty ? <Button variant="ghost" onClick={() => setF({ slug: e.slug, name_he: e.name_he, name_en: e.name_en, aliases: e.aliases, ambiguous: e.ambiguous, ticker: e.ticker ?? '', exchange: e.exchange ?? '', status: e.status })}>{t`Discard changes`}</Button> : null}
          </div>
        )}
      </section>

      <section aria-label={t`Programmes`} className="space-y-2">
        <h3 className="text-sm font-semibold">{t`Programmes and videos (${d.programmes.length})`}</h3>
        {d.programmes.length ? (
          <ul className="max-h-64 space-y-1 overflow-auto text-sm">
            {d.programmes.map((p) => (
              <li key={p.subject_id} className="flex justify-between gap-3 border-b border-border py-1">
                <span className="truncate">{p.title || '—'} <span className="text-fg-muted"><bdi>{fmtDateTime(p.starts_at, o)}</bdi></span>{p.subject_type === 'asset' ? <Badge className="ms-1">{t`Video`}</Badge> : null}</span>
                <span className="shrink-0 text-fg-muted">×{p.count}</span>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-fg-muted">{t`Not mentioned in any indexed programme yet.`}</p>}
      </section>

      <section aria-label={t`Mentions`} className="space-y-2">
        <h3 className="text-sm font-semibold">{t`Latest mentions (${d.mentions.length})`}</h3>
        {!ro && d.mentions.length ? <p className="text-xs text-fg-muted">{t`Tick mentions to move them to a new company with “Split”.`}</p> : null}
        {d.mentions.length ? (
          <ul className="max-h-96 space-y-1 overflow-auto text-sm">
            {d.mentions.map((x) => (
              <li key={x.id} className="flex items-start gap-2 border-b border-border py-1">
                {ro ? null : <input type="checkbox" className="mt-1" aria-label={t`Pick this mention`} checked={picked.has(x.id)}
                  onChange={(ev) => { const n = new Set(picked); if (ev.target.checked) n.add(x.id); else n.delete(x.id); setPicked(n); }} />}
                <span className="min-w-0">
                  <span className="block text-xs text-fg-muted">{x.title} · <bdi>{fmtDateTime(x.starts_at, o)}</bdi> · <bdi dir="ltr">{clock(x.offset_s * 1000)}</bdi> · {t`confidence ${Math.round(x.confidence * 100)}%`}</span>
                  <span className="block">{x.text}</span>
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      <AlertSubscribers id={e.id} />

      {ro ? null : (
        <section aria-label={t`Corrections`} className="space-y-3 rounded-md border border-border p-4">
          <h3 className="text-sm font-semibold">{t`Corrections`}</h3>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-56 flex-1 space-y-1">
              <span className="text-sm font-medium">{t`Merge another company into this one`}</span>
              <Select ariaLabel={t`Company to merge`} value={mergeFrom || undefined} placeholder={t`Pick a company`} onValueChange={setMergeFrom}
                options={others.map((x) => ({ value: x.id, label: `${x.name_he}${x.ticker ? ` (${x.ticker})` : ''} · ${x.mentions}` }))} />
            </div>
            <Button variant="secondary" disabled={!mergeFrom} loading={m.merge.isPending} onClick={() => {
              const from = others.find((x) => x.id === mergeFrom);
              if (!from || !window.confirm(t`Merge “${from.name_he}” into “${e.name_he}”? Its mentions move here, its names become aliases, and it is deleted.`)) return;
              m.merge.mutate({ into: e.id, from: from.id }, {
                onSuccess: (r) => { setMergeFrom(''); toast.push({ title: t`Merged: ${r.moved_mentions} mentions moved`, tone: 'ok' }); },
                onError: (err) => toast.push({ title: t`Could not merge`, description: problemText(err), tone: 'danger' }),
              });
            }}><GitMerge className="size-4" aria-hidden />{t`Merge`}</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setSplitOpen(true)}><Scissors className="size-4" aria-hidden />{t`Split…`}</Button>
            <Button variant="danger" loading={m.remove.isPending} onClick={() => {
              if (!window.confirm(t`Delete “${e.name_he}” and its ${e.mentions} mentions? To keep it out of matching but restorable, set it to Disabled instead.`)) return;
              m.remove.mutate(e.id, {
                onSuccess: () => { toast.push({ title: t`Company deleted`, tone: 'ok' }); onGone(); },
                onError: (err) => toast.push({ title: t`Could not delete`, description: problemText(err), tone: 'danger' }),
              });
            }}><Trash2 className="size-4" aria-hidden />{t`Delete`}</Button>
          </div>
        </section>
      )}
      {splitOpen ? <SplitDialog e={e} picked={[...picked]} onClose={() => setSplitOpen(false)} onDone={(id) => { setSplitOpen(false); setPicked(new Set()); onOpen(id); }} /> : null}
    </div>
  );
}

function SplitDialog({ e, picked, onClose, onDone }: { e: EntityRow; picked: number[]; onClose: () => void; onDone: (id: string) => void }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const toast = useToast();
  const m = useEntityMutations(tenant.slug);
  const [he, setHe] = React.useState('');
  const [en, setEn] = React.useState('');
  const [slug, setSlug] = React.useState('');
  const [ticker, setTicker] = React.useState('');
  const [move, setMove] = React.useState<Set<string>>(new Set());
  const all = [...e.aliases, ...e.ambiguous];
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent title={t`Split “${e.name_he}”`} description={t`A new company takes the aliases you tick (and the ${picked.length} mentions you picked). Mentions follow the wording: the next index pass re-links every mention by the aliases each company has.`}>
        <form className="space-y-3" onSubmit={(ev) => {
          ev.preventDefault();
          m.split.mutate({ id: e.id, body: { name_he: he, name_en: en, slug, ticker, move_aliases: [...move], move_mentions: picked } }, {
            onSuccess: (r) => { toast.push({ title: t`Split: ${r.moved_mentions} mentions moved to ${r.created.name_he}`, tone: 'ok' }); onDone(r.created.id); },
            onError: (err) => toast.push({ title: t`Could not split`, description: problemText(err), tone: 'danger' }),
          });
        }}>
          <Field id="split-he" label={t`Hebrew name of the new company`}><Input value={he} onChange={(x) => setHe(x.target.value)} required /></Field>
          <Field id="split-en" label={t`English name`}><Input value={en} onChange={(x) => setEn(x.target.value)} dir="ltr" /></Field>
          <Field id="split-slug" label={t`Slug`}><Input value={slug} onChange={(x) => setSlug(x.target.value.toLowerCase())} dir="ltr" required pattern="[a-z0-9][a-z0-9\-]{0,63}" /></Field>
          <Field id="split-ticker" label={t`Ticker`}><Input value={ticker} onChange={(x) => setTicker(x.target.value.toUpperCase())} dir="ltr" /></Field>
          <fieldset className="space-y-1">
            <legend className="text-sm font-medium">{t`Aliases to move`}</legend>
            {all.length ? all.map((a) => (
              <label key={a} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={move.has(a)} onChange={(x) => { const n = new Set(move); if (x.target.checked) n.add(a); else n.delete(a); setMove(n); }} />{a}
                {e.ambiguous.includes(a) ? <Badge>{t`ambiguous`}</Badge> : null}
              </label>
            )) : <p className="text-xs text-fg-muted">{t`This company has no aliases.`}</p>}
          </fieldset>
          <div className="flex gap-2">
            <Button type="submit" loading={m.split.isPending} disabled={!he || !slug || (move.size === 0 && picked.length === 0)}>{t`Split`}</Button>
            <Button type="button" variant="ghost" onClick={onClose}>{t`Cancel`}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Company pages per site: a site shows /company/{slug} only once it has a company template (off by default). */
function CompanyTemplates() {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const sites = useSites(tenant.slug);
  if (!sites.data?.length) return null;
  return (
    <section className="space-y-2 rounded-md border border-border p-3" aria-label={t`Company pages`}>
      <h2 className="text-sm font-semibold">{t`Company pages on your sites`}</h2>
      <p className="text-xs text-fg-muted">{t`With a company template a site gets a page per company (/company/…) listing its latest mentions, the chips under the player link to it, and the sitemap lists it. Publish the template in the site editor to make the pages live.`}</p>
      <ul className="space-y-1">{sites.data.map((s) => <SiteTemplateRow key={s.id} site={s} />)}</ul>
    </section>
  );
}

function SiteTemplateRow({ site }: { site: SiteRow }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const toast = useToast();
  const navigate = useNavigate();
  const pages = usePages(tenant.slug, site.id);
  const act = usePageActions(tenant.slug, site.id);
  const tpl = pages.data?.find((p) => p.kind === 'template' && p.template_for === 'company');
  const canCreate = can(scopes, 'sites:write' as Scope);
  const open = (id: string) => void navigate({ to: '/t/$slug/sites/$siteId/pages/$pageId', params: { slug: tenant.slug, siteId: site.id, pageId: id } });
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 text-sm">
      <span className="truncate">{lz(site.name) || site.slug}</span>
      {pages.isLoading ? <Skeleton className="h-6 w-24" /> : tpl ? (
        <span className="flex items-center gap-2">
          {tpl.status?.live_version ? <Badge tone="ok">{t`Live`}</Badge> : <Badge>{t`Not published`}</Badge>}
          <Button size="sm" variant="ghost" onClick={() => open(tpl.id)}>{t`Open`}</Button>
        </span>
      ) : canCreate ? (
        <Button size="sm" variant="secondary" loading={act.create.isPending} onClick={() => act.create.mutate(
          { kind: 'template', template_for: 'company', title: { he: 'חברה', en: 'Company' }, draft: COMPANY_TEMPLATE_DRAFT },
          {
            onSuccess: (p) => { toast.push({ title: t`Company template created as a draft`, tone: 'ok' }); open(p.id); },
            onError: (err) => toast.push({ title: t`Could not create the template`, description: problemText(err), tone: 'danger' }),
          },
        )}>{t`Create the template`}</Button>
      ) : <span className="text-xs text-fg-muted">{t`No company template`}</span>}
    </li>
  );
}


const PII = 'stats:pii' as Scope;

/** v3 §3.2: the tenant's mention-alert switch and limits, subscriber counts per company, erase by address. */
function AlertsSettings({ onOpen }: { onOpen: (id: string) => void }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const toast = useToast();
  const q = useEntityAlertSettings(tenant.slug);
  const m = useEntityAlertMutations(tenant.slug);
  const s = q.data;
  const counts = useEntityAlertCounts(tenant.slug, !!s?.available);
  const canEdit = can(scopes, 'tenant:settings' as Scope);
  const [win, setWin] = React.useState('');
  const [cap, setCap] = React.useState('');
  const [erase, setErase] = React.useState('');
  React.useEffect(() => { if (s) { setWin(String(s.window_minutes)); setCap(String(s.hourly_cap)); } }, [s?.window_minutes, s?.hourly_cap]); // eslint-disable-line react-hooks/exhaustive-deps
  if (q.isLoading) return <Skeleton className="h-32" />;
  if (!s) return null;
  const fail = (title: string) => (err: unknown) => toast.push({ title, description: problemText(err), tone: 'danger' });
  const dirty = s.available && (String(s.window_minutes) !== win || String(s.hourly_cap) !== cap);
  return (
    <section className="space-y-2 rounded-md border border-border p-3" aria-label={t`Mention alerts`}>
      <h2 className="flex items-center gap-2 text-sm font-semibold"><Bell className="size-4" aria-hidden />{t`Mention alerts`}</h2>
      <p className="text-xs text-fg-muted">{t`Viewers follow a company on its company page (or with the <vs-follow> web component) with their e-mail address, confirm by mail, and get at most one mail per window when it is mentioned in a new programme or video, with links to the moments. Every mail has a one-click unsubscribe; each mail also fires the alert.triggered webhook. Off by default.`}</p>
      {!s.available ? <p className="text-xs text-warn">{t`Mention alerts are not available on this ViewStream installation yet.`}</p> : (
        <>
          <label className="flex items-center gap-3 text-sm">
            <Switch checked={s.enabled} disabled={!canEdit || m.save.isPending} aria-label={t`Mention alerts on`}
              onCheckedChange={(v) => m.save.mutate({ enabled: v }, {
                onSuccess: () => toast.push({ title: v ? t`Mention alerts are on` : t`Mention alerts are off`, tone: 'ok' }),
                onError: fail(t`Could not save`),
              })} />
            {s.enabled ? t`On — company pages show the follow form` : t`Off`}
          </label>
          {!s.mail_configured ? <p className="text-xs text-warn">{t`No mail relay is configured: confirmation and alert mails are only logged.`}</p> : null}
          <div className="grid grid-cols-2 gap-2">
            <Field id="alerts-window" label={t`Window (minutes)`} hint={t`At most one mail per subscriber per company within this time (5–10080).`}>
              <Input type="number" min={5} max={10080} value={win} onChange={(e) => setWin(e.target.value)} disabled={!canEdit} dir="ltr" />
            </Field>
            <Field id="alerts-cap" label={t`Mails per hour`} hint={t`All alert mails of your account, confirmations included; above it mails wait for the next hour.`}>
              <Input type="number" min={1} max={100000} value={cap} onChange={(e) => setCap(e.target.value)} disabled={!canEdit} dir="ltr" />
            </Field>
          </div>
          {canEdit ? (
            <Button size="sm" variant="secondary" disabled={!dirty} loading={m.save.isPending} onClick={() => m.save.mutate({ window_minutes: Number(win), hourly_cap: Number(cap) }, {
              onSuccess: () => toast.push({ title: t`Saved`, tone: 'ok' }),
              onError: fail(t`Could not save`),
            })}>{t`Save limits`}</Button>
          ) : null}
          <p className="text-xs text-fg-muted">{t`Alert mails in the last hour: ${s.mails_last_hour}`}</p>
          {counts.data?.length ? (
            <ul className="space-y-1 text-sm" aria-label={t`Subscribers per company`}>
              {counts.data.map((c) => (
                <li key={c.entity_id} className="flex items-center justify-between gap-2">
                  <button type="button" className="truncate text-start hover:underline" onClick={() => onOpen(c.entity_id)}>{c.name_he}</button>
                  <span className="shrink-0 text-xs text-fg-muted">{t`${c.active} active · ${c.pending} pending`}</span>
                </li>
              ))}
            </ul>
          ) : counts.isSuccess ? <p className="text-xs text-fg-muted">{t`No subscribers yet.`}</p> : null}
          {can(scopes, PII) ? (
            <form className="space-y-1 border-t border-border pt-2" onSubmit={(e) => {
              e.preventDefault();
              if (!window.confirm(t`Delete every alert subscription of ${erase}? This cannot be undone.`)) return;
              m.erase.mutate(erase.trim(), {
                onSuccess: (r) => { setErase(''); toast.push({ title: t`Erased: ${r.deleted} subscriptions deleted`, tone: 'ok' }); },
                onError: fail(t`Could not erase the address`),
              });
            }}>
              <label htmlFor="alerts-erase" className="text-xs font-medium">{t`Erase an address (privacy request)`}</label>
              <div className="flex gap-2">
                <Input id="alerts-erase" type="email" value={erase} onChange={(e) => setErase(e.target.value)} dir="ltr" placeholder="viewer@example.com" />
                <Button type="submit" size="md" variant="danger" disabled={!erase.includes('@')} loading={m.erase.isPending}>{t`Erase`}</Button>
              </div>
            </form>
          ) : null}
        </>
      )}
    </section>
  );
}

/** v3 §3.2: one company's alert subscribers — masked addresses, status, CSV export, remove. */
function AlertSubscribers({ id }: { id: string }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { locale } = useLocale();
  const toast = useToast();
  const settings = useEntityAlertSettings(tenant.slug);
  const allowed = can(scopes, PII) && !!settings.data?.available;
  const subs = useEntityAlertSubscribers(tenant.slug, id, allowed);
  const m = useEntityAlertMutations(tenant.slug);
  if (!allowed || (!settings.data?.enabled && !subs.data?.length)) return null;
  const o = { locale, tz: 'Asia/Jerusalem' };
  const label = (st: EntityAlertStatus) => (st === 'active' ? t`Active` : st === 'pending' ? t`Waiting for confirmation` : t`Unsubscribed`);
  return (
    <section aria-label={t`Alert subscribers`} className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold"><Bell className="size-4" aria-hidden />{t`Alert subscribers (${subs.data?.length ?? 0})`}</h3>
        {subs.data?.length ? (
          <Button size="sm" variant="ghost" onClick={() => void downloadFile(alertSubscribersCsvPath(id), 'alert-subscribers.csv').catch((err: unknown) =>
            toast.push({ title: t`Could not export`, description: String((err as Error)?.message ?? err), tone: 'danger' }))}>
            <Download className="size-4" aria-hidden />{t`Export CSV`}
          </Button>
        ) : null}
      </div>
      <p className="text-xs text-fg-muted">{t`Addresses are masked here and in the export. A subscription is active only after the viewer confirmed the mail.`}</p>
      {subs.isLoading ? <Skeleton className="h-24" /> : subs.data?.length ? (
        <ul className="max-h-64 space-y-1 overflow-auto text-sm">
          {subs.data.map((x) => (
            <li key={x.id} className="flex items-center justify-between gap-3 border-b border-border py-1">
              <span className="min-w-0">
                <bdi dir="ltr" className="font-medium">{x.email}</bdi>{' '}
                <Badge tone={x.status === 'active' ? 'ok' : x.status === 'pending' ? 'warn' : 'neutral'}>{label(x.status)}</Badge>
                <span className="block text-xs text-fg-muted">
                  {t`requested`} <bdi>{fmtDateTime(x.requested_at, o)}</bdi>
                  {x.last_sent_at ? <> · {t`last mail`} <bdi>{fmtDateTime(x.last_sent_at, o)}</bdi></> : null}
                  {x.sent_count ? <> · {t`${x.sent_count} mails`}</> : null}
                </span>
              </span>
              <Button size="sm" variant="ghost" aria-label={t`Remove ${x.email}`} loading={m.remove.isPending && m.remove.variables === x.id} onClick={() => {
                if (!window.confirm(t`Remove ${x.email}? No more alerts go to this address for this company.`)) return;
                m.remove.mutate(x.id, {
                  onSuccess: () => toast.push({ title: t`Subscriber removed`, tone: 'ok' }),
                  onError: (err) => toast.push({ title: t`Could not remove`, description: problemText(err), tone: 'danger' }),
                });
              }}><Trash2 className="size-4" aria-hidden /></Button>
            </li>
          ))}
        </ul>
      ) : <p className="text-sm text-fg-muted">{t`No one follows this company yet.`}</p>}
    </section>
  );
}
